import { useEffect, useState } from 'react';
import {
  BROWSER_PREFERENCES_STORAGE_KEY,
  readBrowserPreference,
  writeBrowserPreference,
} from './browserPreferences';

export type ResolvedTheme = 'light' | 'dark';
export type ThemePreference = 'system' | ResolvedTheme;

const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)';
const DARK_THEME_COLOR = '#07131d';
const LIGHT_THEME_COLOR = '#f2f6fa';
const THEME_PREFERENCE_EVENT = 'perspectify:theme-preference-change';

function resolveSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }

  return window.matchMedia(SYSTEM_THEME_QUERY).matches ? 'dark' : 'light';
}

function updateThemeColor(theme: ResolvedTheme) {
  const metaThemeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!metaThemeColor) {
    return;
  }

  metaThemeColor.setAttribute('content', theme === 'dark' ? DARK_THEME_COLOR : LIGHT_THEME_COLOR);
}

export function readThemePreference(): ThemePreference {
  return readBrowserPreference('themeMode') ?? 'system';
}

export function resolveThemePreference(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? resolveSystemTheme() : preference;
}

export function applyResolvedTheme(theme: ResolvedTheme) {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  root.classList.toggle('dark', theme === 'dark');
  updateThemeColor(theme);
}

export function applyThemePreference(preference: ThemePreference) {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.dataset.themePreference = preference;
  applyResolvedTheme(resolveThemePreference(preference));
}

export function setThemePreference(preference: ThemePreference) {
  if (typeof window === 'undefined') {
    return;
  }

  writeBrowserPreference('themeMode', preference);
  applyThemePreference(preference);
  window.dispatchEvent(new CustomEvent<ThemePreference>(THEME_PREFERENCE_EVENT, { detail: preference }));
}

export function useThemePreference() {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() =>
    readThemePreference()
  );

  useEffect(() => {
    const syncThemePreference = () => {
      setThemePreferenceState(readThemePreference());
    };

    const handleThemePreferenceChange = (event: Event) => {
      const nextPreference =
        event instanceof CustomEvent && event.detail ? event.detail : readThemePreference();
      setThemePreferenceState(nextPreference);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== BROWSER_PREFERENCES_STORAGE_KEY) {
        return;
      }
      syncThemePreference();
    };

    window.addEventListener(THEME_PREFERENCE_EVENT, handleThemePreferenceChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(THEME_PREFERENCE_EVENT, handleThemePreferenceChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return {
    themePreference,
    resolvedTheme: resolveThemePreference(themePreference),
    setThemePreference,
  };
}

export function ThemeController(): null {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      applyThemePreference('dark');
      return undefined;
    }

    const mediaQuery = window.matchMedia(SYSTEM_THEME_QUERY);
    const syncTheme = () => applyThemePreference(readThemePreference());
    const handleSystemThemeChange = () => {
      if (readThemePreference() === 'system') {
        syncTheme();
      }
    };

    syncTheme();
    window.addEventListener(THEME_PREFERENCE_EVENT, syncTheme);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => {
        window.removeEventListener(THEME_PREFERENCE_EVENT, syncTheme);
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      };
    }

    mediaQuery.addListener(handleSystemThemeChange);
    return () => {
      window.removeEventListener(THEME_PREFERENCE_EVENT, syncTheme);
      mediaQuery.removeListener(handleSystemThemeChange);
    };
  }, []);

  return null;
}
